import { TuiButtonLoading } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, finalize, of, startWith, switchMap } from 'rxjs';
import { TuiDialogContext, TuiButton } from '@taiga-ui/core';
import { TuiDay, TuiTime } from '@taiga-ui/cdk';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { RequestService } from '@core/services/request/request.service';
import { XAlertService } from '@core/services/x-alert.service';
import { RequestFormFilesCommonComponent } from '@features/requests/request-form/components/request-form-files/components';
import { RequestFilesPersonType } from '@features/requests/request-form/components/request-form-files/types';
import type {
  CarBrand,
  Counterparty,
  CounterpartySubdivision,
  CreatedRequestResult,
  CreateRequestItemPayload,
  CreateRequestsBatchPayload,
  Product,
} from '@core/types';
import { RequestCreateStep1aComponent } from './steps/step-1a-counterparty/step-1a-counterparty.component';
import { RequestCreateStep1bComponent } from './steps/step-1b-vehicle-delivery/step-1b-vehicle-delivery.component';
import { RequestCreateStep1cComponent } from './steps/step-1c-managers-products/step-1c-managers-products.component';
import { RequestCreateSuccessComponent } from './steps/step-success/step-success.component';
import type {
  RequestCreateExtraFieldControlValue,
  RequestCreateFieldName,
  RequestCreateFormStep,
  RequestCreateProductRow,
  RequestCreateRequestTypeOption,
  RequestCreateStep,
} from './request-create.types';
import {
  LOGISTIC_REQUEST_TYPE_LABELS,
  LogisticRequestExtraField,
  LogisticRequestType,
} from '@outkeep/shared';

const STEP_FIELDS: Record<RequestCreateFormStep, RequestCreateFieldName[]> = {
  'form-1a': [
    'counterpartyOwnerId',
    'nearestBranch',
    'projectNumber',
    'contract',
    'contractDate',
    'contractPlannedEndDate',
    'contractEarlyEndDate',
    'subjName',
    'subjInn',
    'subjFullname',
    'subjPhone',
  ],
  'form-1b': [
    'carBrandId',
    'carModel',
    'carVin',
    'carPlantNumber',
    'carYear',
    'areaCode',
    'deliveryName',
    'deliveryAddress',
    'deliveryPersonName',
    'deliveryPersonPhone',
    'deliveryPersonEmail',
    'releaseDate',
    'releaseTime',
  ],
  'form-1c': [
    'requestType',
    'supportManagerFullname',
    'supportManagerPhone',
    'clientManagerFullname',
    'clientManagerPhone',
    'clientManagerEmail',
    'shipmentDocsAddress',
    'shipmentKeysAddress',
    'timezone',
    'contractorCounterpartyId',
    'contractorCounterpartySubdivisionId',
    'installPrice',
  ],
};

const FIELD_LABELS: Record<RequestCreateFieldName, string> = {
  counterpartyOwnerId: 'Заказчик',
  nearestBranch: 'Точка заказчика',
  projectNumber: 'Номер проекта',
  contract: 'Номер договора лизинга',
  contractDate: 'Дата договора лизинга',
  contractPlannedEndDate: 'Плановая дата окончания договора',
  contractEarlyEndDate: 'Дата досрочного окончания договора',
  subjName: 'Наименование лизингополучателя',
  subjInn: 'ИНН',
  subjFullname: 'ФИО',
  subjPhone: 'Телефон',
  carBrandId: 'Марка ТС',
  carBrandName: 'Наименование марки ТС',
  carModel: 'Модель ТС',
  carVin: 'VIN',
  carPlantNumber: 'Заводской номер',
  carYear: 'Год выпуска ТС',
  areaCode: 'Код субъекта',
  deliveryName: 'Название салона выдачи',
  deliveryAddress: 'Адрес салона выдачи',
  deliveryPersonName: 'Представитель поставщика',
  deliveryPersonPhone: 'Телефон контактного лица',
  deliveryPersonEmail: 'Email контактного лица',
  releaseDate: 'Планируемая дата выдачи',
  releaseTime: 'Планируемое время выдачи',
  requestType: 'Тип заявки',
  supportManagerFullname: 'ФИО менеджера сопровождения',
  supportManagerPhone: 'Телефон менеджера сопровождения',
  clientManagerFullname: 'ФИО менеджера клиента',
  clientManagerPhone: 'Телефон менеджера клиента',
  clientManagerEmail: 'Email менеджера клиента',
  shipmentDocsAddress: 'Адрес отправки оригиналов документов',
  shipmentKeysAddress: 'Адрес отправки ключей',
  timezone: 'Часовой пояс',
  contractorCounterpartyId: 'Назначенный исполнитель',
  contractorCounterpartySubdivisionId: 'Назначенное подразделение',
  installPrice: 'Цена установки',
};

@Component({
  selector: 'x-request-create-dialog',
  templateUrl: './request-create-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TuiButton,
    TranslatePipe,
    RequestCreateStep1aComponent,
    RequestCreateStep1bComponent,
    RequestCreateStep1cComponent,
    RequestFormFilesCommonComponent,
    RequestCreateSuccessComponent,
    TuiButtonLoading,
  ],
})
export class RequestCreateDialogComponent {
  private readonly context = inject<TuiDialogContext>(POLYMORPHEUS_CONTEXT);
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly requestService = inject(RequestService);
  private readonly router = inject(Router);
  private readonly alertService = inject(XAlertService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly $step = signal<RequestCreateStep>('form-1a');
  readonly $isSubmitting = signal(false);
  readonly $isSendingToWork = signal(false);
  readonly $createdRequests = signal<CreatedRequestResult[]>([]);
  readonly $invalidFieldLabels = signal<string[]>([]);

  readonly $counterparties = signal<Counterparty[]>([]);
  readonly $carBrands = signal<CarBrand[]>([]);
  readonly $contractors = signal<Counterparty[]>([]);
  readonly $subdivisions = signal<CounterpartySubdivision[]>([]);
  readonly $availableProducts = signal<Product[]>([]);
  readonly $selectedProducts = signal<RequestCreateProductRow[]>([]);
  readonly $requestTypeOptions = signal<RequestCreateRequestTypeOption[]>([]);
  readonly $extraFields = signal<LogisticRequestExtraField[]>([]);
  readonly $selectedRequestType = signal<LogisticRequestType>(
    LogisticRequestType.Default,
  );
  readonly $visibleExtraFields = computed(() =>
    this.$extraFields().filter(
      field =>
        !field.requestType || field.requestType === this.$selectedRequestType(),
    ),
  );
  readonly RequestFilesPersonType = RequestFilesPersonType;
  readonly steps: { step: RequestCreateStep; label: string }[] = [
    { step: 'form-1a', label: '1' },
    { step: 'form-1b', label: '2' },
    { step: 'form-1c', label: '3' },
    { step: 'files', label: '4' },
  ];

  readonly extraForm = new FormGroup<
    Record<string, FormControl<RequestCreateExtraFieldControlValue>>
  >({});

  readonly form = new FormGroup({
    counterpartyOwnerId: new FormControl<number | null>(
      null,
      Validators.required,
    ),
    nearestBranch: new FormControl<string | null>(null),
    projectNumber: new FormControl(''),
    contract: new FormControl(''),
    contractDate: new FormControl<TuiDay | null>(null),
    contractPlannedEndDate: new FormControl<TuiDay | null>(null),
    contractEarlyEndDate: new FormControl<TuiDay | null>(null),
    subjName: new FormControl(''),
    subjInn: new FormControl(''),
    subjFullname: new FormControl(''),
    subjPhone: new FormControl(''),
    carBrandId: new FormControl<number | null>(null),
    carBrandName: new FormControl(''),
    carModel: new FormControl(''),
    carVin: new FormControl(''),
    carPlantNumber: new FormControl(''),
    carYear: new FormControl(''),
    areaCode: new FormControl<number | null>(null),
    deliveryName: new FormControl(''),
    deliveryAddress: new FormControl(''),
    deliveryPersonName: new FormControl(''),
    deliveryPersonPhone: new FormControl(''),
    deliveryPersonEmail: new FormControl(''),
    releaseDate: new FormControl<TuiDay | null>(null),
    releaseTime: new FormControl<TuiTime | null>(null),
    requestType: new FormControl<LogisticRequestType>(
      LogisticRequestType.Default,
      {
        nonNullable: true,
        validators: Validators.required,
      },
    ),
    supportManagerFullname: new FormControl(''),
    supportManagerPhone: new FormControl(''),
    clientManagerFullname: new FormControl(''),
    clientManagerPhone: new FormControl(''),
    clientManagerEmail: new FormControl(''),
    shipmentDocsAddress: new FormControl(''),
    shipmentKeysAddress: new FormControl(''),
    timezone: new FormControl(''),
    contractorCounterpartyId: new FormControl<number | null>(null),
    contractorCounterpartySubdivisionId: new FormControl<number | null>(null),
    installPrice: new FormControl(''),
  });

  constructor() {
    this.initReferenceData();
    this.initFormReactions();
  }

  private initReferenceData(): void {
    this.requestsApiService
      .getMyRequestCounterparties$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(list => {
        this.$counterparties.set(list);
        if (list.length === 1) {
          this.form.controls.counterpartyOwnerId.setValue(list[0].id);
        }
      });

    this.requestsApiService
      .getCarBrands$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(brands => this.$carBrands.set(brands));

    this.requestsApiService
      .getAvailableRequestTypes$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(requestTypes => {
        const selectedType = requestTypes.includes(LogisticRequestType.Default)
          ? LogisticRequestType.Default
          : (requestTypes[0] ?? LogisticRequestType.Default);

        this.$requestTypeOptions.set(
          requestTypes.map(value => ({
            value,
            label: LOGISTIC_REQUEST_TYPE_LABELS[value],
          })),
        );
        this.form.controls.requestType.setValue(selectedType);
        this.$selectedRequestType.set(selectedType);
      });

    this.requestsApiService
      .getRequestExtraFields$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(fields => {
        this.$extraFields.set(fields);
        this.ensureExtraFieldControls(fields);
      });
  }

  private initFormReactions(): void {
    this.form.controls.requestType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(requestType => {
        this.$selectedRequestType.set(requestType);
        this.extraForm.reset();
      });

    this.form.controls.counterpartyOwnerId.valueChanges
      .pipe(
        switchMap(id =>
          id
            ? this.requestsApiService.getAvailableContractors$(id)
            : of([] as Counterparty[]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(contractors => {
        this.$contractors.set(contractors);
        this.form.controls.contractorCounterpartySubdivisionId.setValue(null);

        const xKeeper =
          contractors.find(contractor =>
            (contractor.name ?? '').toLowerCase().includes('x-keeper'),
          ) ?? (contractors.length === 1 ? contractors[0] : null);

        this.form.controls.contractorCounterpartyId.setValue(
          xKeeper?.id ?? null,
        );
      });

    combineLatest([
      this.form.controls.counterpartyOwnerId.valueChanges.pipe(
        startWith(this.form.controls.counterpartyOwnerId.value),
      ),
      this.form.controls.requestType.valueChanges.pipe(
        startWith(this.form.controls.requestType.value),
      ),
    ])
      .pipe(
        switchMap(([counterpartyOwnerId, requestType]) =>
          this.requestsApiService.getAvailableProducts$({
            forCounterpartyId: counterpartyOwnerId ?? undefined,
            requestTypes: [requestType],
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(products => {
        this.$availableProducts.set(products);
        this.$selectedProducts.set([]);
      });

    this.form.controls.contractorCounterpartyId.valueChanges
      .pipe(
        switchMap(id =>
          id
            ? this.requestsApiService.getContractorSubdivisions$(id)
            : of([] as CounterpartySubdivision[]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(subdivisions => this.$subdivisions.set(subdivisions));
  }

  addProduct(productId: number | null, amount: number | null): void {
    if (!productId || !amount || amount < 1) return;

    const product = this.$availableProducts().find(p => p.id === productId);
    const currentRows = this.$selectedProducts();

    if (!product || currentRows.some(row => row.productId === productId)) {
      return;
    }

    this.$selectedProducts.set([
      ...currentRows,
      { productId, amount, name: product.name },
    ]);
  }

  removeProduct(productId: number): void {
    this.$selectedProducts.set(
      this.$selectedProducts().filter(row => row.productId !== productId),
    );
  }

  goNext(): void {
    const step = this.$step();

    if (step === 'form-1a' || step === 'form-1b') {
      if (!this.validateStep(step)) return;
      this.$step.set(step === 'form-1a' ? 'form-1b' : 'form-1c');

      return;
    }

    if (step === 'form-1c') {
      if (!this.validateAllAndNavigate()) return;
      this.submit();
    }
  }

  goBack(): void {
    const step = this.$step();

    this.$invalidFieldLabels.set([]);

    if (step === 'form-1b') this.$step.set('form-1a');
    else if (step === 'form-1c') this.$step.set('form-1b');
  }

  navigateToRequest(): void {
    const created = this.$createdRequests()[0];

    if (created) {
      void this.router.navigate([
        '/requests',
        created.requestNumber,
        'summary',
      ]);
      this.context.completeWith(undefined);
    }
  }

  close(): void {
    this.context.completeWith(undefined);
  }

  finishFilesStep(): void {
    this.$step.set('success');
  }

  sendToWork(): void {
    const created = this.$createdRequests()[0];

    if (!created || this.$isSendingToWork()) return;

    this.$isSendingToWork.set(true);
    this.requestsApiService
      .sendRequestToWork$(created.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.$isSendingToWork.set(false)),
      )
      .subscribe({
        next: () => {
          this.alertService.success(
            this.translateService.instant(
              'request_create.send_to_work_success',
            ),
          );
          this.$step.set('success');
        },
        error: err => {
          this.alertService.error(
            err?.error?.message ||
              this.translateService.instant(
                'request_create.send_to_work_error',
              ),
          );
        },
      });
  }

  private validateStep(step: RequestCreateFormStep): boolean {
    STEP_FIELDS[step].forEach(field => {
      this.form.controls[field].markAsTouched();
    });

    const invalidFields = STEP_FIELDS[step].filter(
      field => this.form.controls[field].invalid,
    );

    return this.handleInvalidFields(invalidFields);
  }

  private validateAllAndNavigate(): boolean {
    this.form.markAllAsTouched();

    const invalidFields = Object.keys(this.form.controls).filter(
      (field): field is RequestCreateFieldName =>
        this.form.controls[field as RequestCreateFieldName].invalid,
    );

    if (!invalidFields.length) {
      this.$invalidFieldLabels.set([]);

      return true;
    }

    const firstInvalidStep = (
      Object.entries(STEP_FIELDS) as [
        RequestCreateFormStep,
        RequestCreateFieldName[],
      ][]
    ).find(([, fields]) =>
      fields.some(field => invalidFields.includes(field)),
    )?.[0];

    if (firstInvalidStep && firstInvalidStep !== this.$step()) {
      this.$step.set(firstInvalidStep);
    }

    return this.handleInvalidFields(invalidFields);
  }

  private handleInvalidFields(fields: RequestCreateFieldName[]): boolean {
    const labels = fields.map(field => FIELD_LABELS[field]);

    this.$invalidFieldLabels.set(labels);

    if (!labels.length) return true;

    this.alertService.error(
      `Заполните обязательные поля: ${labels.join(', ')}`,
    );

    return false;
  }

  private submit(): void {
    if (this.$isSubmitting()) return;

    const payload = this.buildPayload();

    this.$isSubmitting.set(true);
    this.requestsApiService
      .createRequests$(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.$isSubmitting.set(false)),
      )
      .subscribe({
        next: created => {
          const firstCreated = created[0];

          if (!firstCreated?.requestNumber) {
            this.alertService.error(
              'Не удалось получить номер созданной заявки',
            );

            return;
          }

          this.$createdRequests.set(created);
          void this.requestService.refreshCurrentRequestsList();
          this.requestService.loadRequest(firstCreated.requestNumber);
          this.$step.set('files');
        },
        error: err => {
          this.alertService.error(
            err?.error?.message || 'Ошибка при создании заявки',
          );
        },
      });
  }

  private buildPayload(): CreateRequestsBatchPayload {
    const v = this.form.getRawValue();

    return {
      counterpartyOwnerId: v.counterpartyOwnerId!,
      requestsData: [
        this.stripEmpty<CreateRequestItemPayload>({
          requestType: v.requestType,
          extra: this.buildExtraPayload(),
          projectNumber: v.projectNumber,
          contract: v.contract,
          contractDate: this.toDateString(v.contractDate),
          contractPlannedEndDate: this.toDateString(v.contractPlannedEndDate),
          contractEarlyEndDate: this.toDateString(v.contractEarlyEndDate),
          releaseDate: this.toDateString(v.releaseDate),
          releaseTime: v.releaseTime?.toString(),
          nearestBranch: v.nearestBranch,
          subjName: v.subjName,
          subjFullname: v.subjFullname,
          subjPhone: v.subjPhone,
          subjInn: v.subjInn,
          carBrandId: v.carBrandId,
          carBrandName: v.carBrandName,
          carModel: v.carModel,
          carVin: v.carVin,
          carPlantNumber: v.carPlantNumber,
          carYear: v.carYear,
          areaCode: v.areaCode,
          deliveryName: v.deliveryName,
          deliveryAddress: v.deliveryAddress,
          deliveryPersonName: v.deliveryPersonName,
          deliveryPersonPhone: v.deliveryPersonPhone,
          deliveryPersonEmail: v.deliveryPersonEmail,
          shipmentDocsAddress: v.shipmentDocsAddress,
          shipmentKeysAddress: v.shipmentKeysAddress,
          timezone: v.timezone,
          contractorCounterpartyId: v.contractorCounterpartyId,
          contractorCounterpartySubdivisionId:
            v.contractorCounterpartySubdivisionId,
          supportManagerFullname: v.supportManagerFullname,
          supportManagerPhone: v.supportManagerPhone,
          clientManagerFullname: v.clientManagerFullname,
          clientManagerPhone: v.clientManagerPhone,
          clientManagerEmail: v.clientManagerEmail,
          installPrice: v.installPrice,
        }),
      ],
      products: this.$selectedProducts().map(({ productId, amount }) => ({
        productId,
        amount,
      })),
    };
  }

  private stripEmpty<T>(obj: Record<string, unknown>): T {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([, value]) => value !== null && value !== undefined && value !== '',
      ),
    ) as T;
  }

  private ensureExtraFieldControls(fields: LogisticRequestExtraField[]): void {
    fields.forEach(field => {
      if (!this.extraForm.controls[field.name]) {
        this.extraForm.addControl(
          field.name,
          new FormControl<RequestCreateExtraFieldControlValue>(null),
        );
      }
    });
  }

  private buildExtraPayload(): Record<string, unknown> {
    return this.stripEmpty(
      Object.fromEntries(
        this.$visibleExtraFields().map(field => {
          const value = this.extraForm.controls[field.name]?.value;

          return [field.name, value instanceof TuiDay ? value.toJSON() : value];
        }),
      ),
    );
  }

  private toDateString(date: TuiDay | null): string | undefined {
    return date?.toJSON() || undefined;
  }
}
